import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  Plus,
  Edit2,
  Trash2,
  Target,
  ArrowLeft,
  Trophy,
  Search,
  Filter,
  AlertTriangle,
  BarChart3,
  Settings
} from 'lucide-react';
import { TipoAcaoModal } from '../../../components/modals/TipoAcaoModal';
import { CampoAcaoModal } from '../../../components/modals/CampoAcaoModal';
import { api } from '../../../services/api';
import { GrupoTipoAcao } from '../../../types/grupos';

export function GerenciarTiposAcoes() {
  const { grupoId } = useParams<{ grupoId: string }>();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [tiposAcoes, setTiposAcoes] = useState<GrupoTipoAcao[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [tipoAcaoModalOpen, setTipoAcaoModalOpen] = useState(false);
  const [tipoAcaoToEdit, setTipoAcaoToEdit] = useState<GrupoTipoAcao | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [tipoAcaoToDelete, setTipoAcaoToDelete] = useState<GrupoTipoAcao | null>(null);

  // Modal de campos
  const [campoModalOpen, setCampoModalOpen] = useState(false);
  const [tipoAcaoParaCampos, setTipoAcaoParaCampos] = useState<GrupoTipoAcao | null>(null);

  useEffect(() => {
    if (grupoId) {
      loadTiposAcoes();
    }
  }, [grupoId]);

  const loadTiposAcoes = async () => {
    if (!grupoId) return;

    setLoading(true);
    try {
      const response = await api.get(`/grupos-tipos-acoes/grupo/${grupoId}`);
      setTiposAcoes(response.data);
    } catch (error: any) {
      console.error('Erro ao carregar tipos de ações:', error);
      enqueueSnackbar('Erro ao carregar tipos de ações', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = (tipoAcao: GrupoTipoAcao) => {
    setTipoAcaoToEdit(tipoAcao);
    setTipoAcaoModalOpen(true);
  };

  const handleOpenCreateModal = () => {
    setTipoAcaoToEdit(null);
    setTipoAcaoModalOpen(true);
  };

  const handleCloseModal = () => {
    setTipoAcaoModalOpen(false);
    setTipoAcaoToEdit(null);
  };

  const handleModalSuccess = () => {
    loadTiposAcoes();
  };

  const handleDeleteClick = (tipoAcao: GrupoTipoAcao) => {
    setTipoAcaoToDelete(tipoAcao);
    setDeleteConfirmOpen(true);
  };

  const handleOpenCamposModal = (tipoAcao: GrupoTipoAcao) => {
    setTipoAcaoParaCampos(tipoAcao);
    setCampoModalOpen(true);
  };

  const handleCloseCamposModal = () => {
    setCampoModalOpen(false);
    setTipoAcaoParaCampos(null);
  };

  const handleCamposModalSuccess = () => {
    // Pode recarregar dados se necessário
  };

  const handleConfirmDelete = async () => {
    if (!tipoAcaoToDelete) return;

    try {
      await api.delete(`/grupos-tipos-acoes/${tipoAcaoToDelete.id}`);
      enqueueSnackbar('Tipo de ação excluído com sucesso!', { variant: 'success' });
      loadTiposAcoes();
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao excluir tipo de ação',
        { variant: 'error' }
      );
    } finally {
      setDeleteConfirmOpen(false);
      setTipoAcaoToDelete(null);
    }
  };

  // Filtrar tipos de ações por busca
  const filteredTiposAcoes = tiposAcoes.filter(tipoAcao =>
    tipoAcao.descricao.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Estatísticas
  const totalPontuacao = tiposAcoes.reduce((sum, tipo) => sum + tipo.pontuacao_meta, 0);
  const mediaPontuacao = tiposAcoes.length > 0 ? Math.round(totalPontuacao / tiposAcoes.length) : 0;

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/configuracoes/grupos')}
              className="p-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-foreground">Gerenciar Tipos de Ações</h1>
              {tiposAcoes.length > 0 && tiposAcoes[0].grupo && (
                <p className="text-muted-foreground">Grupo: {tiposAcoes[0].grupo.grupo}</p>
              )}
            </div>
          </div>
          <button
            onClick={handleOpenCreateModal}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            <Plus size={20} />
            <span>Nova Ação</span>
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-card p-4 rounded-lg shadow border">
          <div className="flex items-center space-x-3">
            <Target className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-sm text-muted-foreground">Total de Ações</p>
              <p className="text-xl font-semibold text-foreground">{tiposAcoes.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-card p-4 rounded-lg shadow border">
          <div className="flex items-center space-x-3">
            <Trophy className="w-8 h-8 text-yellow-500" />
            <div>
              <p className="text-sm text-muted-foreground">Total de Pontos</p>
              <p className="text-xl font-semibold text-foreground">{totalPontuacao}</p>
            </div>
          </div>
        </div>

        <div className="bg-card p-4 rounded-lg shadow border">
          <div className="flex items-center space-x-3">
            <BarChart3 className="w-8 h-8 text-green-500" />
            <div>
              <p className="text-sm text-muted-foreground">Média de Pontos</p>
              <p className="text-xl font-semibold text-foreground">{mediaPontuacao}</p>
            </div>
          </div>
        </div>

        <div className="bg-card p-4 rounded-lg shadow border">
          <div className="flex items-center space-x-3">
            <Filter className="w-8 h-8 text-purple-500" />
            <div>
              <p className="text-sm text-muted-foreground">Ações Ativas</p>
              <p className="text-xl font-semibold text-foreground">{filteredTiposAcoes.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-card p-4 rounded-lg shadow border mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={20} />
          <input
            type="text"
            placeholder="Buscar por descrição da ação..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Tipos de Ações List */}
      <div className="bg-card rounded-lg shadow border">
        {loading ? (
          <div className="p-8 text-center">
            <div className="text-muted-foreground">Carregando...</div>
          </div>
        ) : filteredTiposAcoes.length === 0 ? (
          <div className="p-8 text-center">
            <Target className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium text-foreground mb-2">
              {searchTerm
                ? 'Nenhuma ação encontrada'
                : 'Nenhuma ação cadastrada'
              }
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm
                ? 'Tente ajustar os termos de busca'
                : 'Comece criando a primeira ação para este grupo'
              }
            </p>
            {!searchTerm && (
              <button
                onClick={handleOpenCreateModal}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                <Plus size={20} />
                <span>Criar Primeira Ação</span>
              </button>
            )}
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredTiposAcoes.map((tipoAcao) => (
              <div key={tipoAcao.id} className="p-4 hover:bg-muted transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Target className="w-5 h-5 text-blue-500" />
                      <h3 className="text-lg font-medium text-foreground">{tipoAcao.descricao}</h3>
                      <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300 rounded-full flex items-center space-x-1">
                        <Trophy size={12} />
                        <span>{tipoAcao.pontuacao_meta} pts</span>
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      Criado em {new Date(tipoAcao.criado_em).toLocaleDateString('pt-BR')}
                    </p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handleOpenCamposModal(tipoAcao)}
                      className="p-2 text-purple-500 hover:bg-muted rounded-md transition-colors"
                      title="Configurar campos"
                    >
                      <Settings size={16} />
                    </button>
                    <button
                      onClick={() => handleOpenEditModal(tipoAcao)}
                      className="p-2 text-blue-500 hover:bg-muted rounded-md transition-colors"
                      title="Editar ação"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button
                      onClick={() => handleDeleteClick(tipoAcao)}
                      className="p-2 text-red-500 hover:bg-muted dark:hover:bg-red-900/20 rounded-md transition-colors"
                      title="Excluir ação"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modals */}
      <TipoAcaoModal
        open={tipoAcaoModalOpen}
        onClose={handleCloseModal}
        onSuccess={handleModalSuccess}
        grupoId={grupoId!}
        tipoAcaoToEdit={tipoAcaoToEdit}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && tipoAcaoToDelete && (
        <div className="fixed inset-0 bg-black/50 z-[9999] flex items-center justify-center">
          <div className="bg-background border border-border rounded-lg shadow-lg p-6 w-full max-w-md">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <h2 className="text-xl font-semibold text-foreground">Confirmar Exclusão</h2>
            </div>

            <p className="text-muted-foreground mb-6">
              Tem certeza que deseja excluir a ação <strong>"{ tipoAcaoToDelete.descricao}"</strong>?
              Esta ação não pode ser desfeita.
            </p>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setDeleteConfirmOpen(false)}
                className="px-4 py-2 text-sm font-medium text-foreground bg-background border border-border rounded-md hover:bg-muted"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmDelete}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700"
              >
                Excluir
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Campos */}
      {tipoAcaoParaCampos && (
        <CampoAcaoModal
          open={campoModalOpen}
          onClose={handleCloseCamposModal}
          onSuccess={handleCamposModalSuccess}
          tipoAcaoId={tipoAcaoParaCampos.id}
          tipoAcaoDescricao={tipoAcaoParaCampos.descricao}
        />
      )}
    </div>
  );
}
